import { onTrack } from '../event-listeners/onTrack';
import { eventBus } from '../event-manager';
import JitsiMeetJS from '../lib-jitsi-meet';
import {
  type AudioInputDevice,
  getLocalTrack,
  muteAudioTrack,
  pauseVideoTrack,
  useBaseStore,
  useConferenceStore,
  useConfigStore,
  type VideoInputDevice,
} from '../store';
import type { CameraFacingInternal, ITrackOptions } from '../types';
import {
  CAMERA_FACING_INTERNAL,
  INTERNAL_EVENTS,
  MEDIA_TYPE,
  SESSION_TYPE,
  tryCatch,
} from '../utils';

/**
 * Gets the real device ID for a 'default' device by matching groupId.
 * This is needed because of a Chrome bug where passing 'default' to getUserMedia
 * doesn't return the correct MediaStreamTrack.
 * @see https://bugs.chromium.org/p/chromium/issues/detail?id=997689
 */
function getRealDeviceId(
  deviceId: string | null,
  type: 'audio' | 'video',
): string | null {
  if (deviceId !== 'default') {
    return deviceId;
  }

  const devices =
    type === 'audio'
      ? useBaseStore.getState().audioInputDevices
      : useBaseStore.getState().videoInputDevices;

  const defaultDevice = devices.find(d => d.deviceId === 'default');
  if (!defaultDevice) {
    return deviceId;
  }

  // Find the device with a matching groupId that isn't the 'default' device
  const matchingDevice = devices.find(
    d => d.deviceId !== 'default' && d.groupId === defaultDevice.groupId,
  );

  return matchingDevice?.deviceId ?? deviceId;
}

export async function createLocalTrackF(
  type: 'audio' | 'video',
  deviceId: string | null = null,
  timeout: number | null = null,
  additionalOptions: ITrackOptions = {},
) {
  const iAmRecorder = useConfigStore.getState().iAmRecorder;
  if (iAmRecorder) {
    return;
  }
  const { data: tracks, error } = await tryCatch<any>(
    JitsiMeetJS.createLocalTracks({
      devices: [type],
      cameraDeviceId: deviceId,
      micDeviceId: deviceId,
      timeout: timeout,
      ...additionalOptions,
    }),
  );
  if (error) {
    throw error;
  }
  return tracks[0];
}

export async function createLocalTrack(
  type: 'audio' | 'video',
  deviceId: string | null = null,
  cameraFacing: CameraFacingInternal = CAMERA_FACING_INTERNAL.USER,
) {
  const isMobile = useBaseStore.getState().isMobile();
  const effectiveDeviceId = isMobile ? null : deviceId;

  // When 'default' is selected, pass the real device ID to getUserMedia
  // to work around Chrome bug: https://bugs.chromium.org/p/chromium/issues/detail?id=997689
  const realDeviceId = getRealDeviceId(effectiveDeviceId, type);

  const { data: track, error } = await tryCatch<any>(
    createLocalTrackF(type, realDeviceId, null, {
      facingMode: cameraFacing,
    }),
  );
  if (track) {
    if (effectiveDeviceId === 'default') {
      track._realDeviceId = track.deviceId = 'default';
    }
    onTrack(track);
  } else {
    if (type === 'video') {
      pauseVideoTrack();
    } else if (type === 'audio') {
      muteAudioTrack();
    }
    if (error instanceof Error) {
      if (error.name === 'gum.permission_denied') {
        eventBus.publish({
          type: INTERNAL_EVENTS.notification,
          payload: {
            type: 'error',
            message: `Permission denied for ${type} track. Please allow access to your device.`,
          },
        });
      }
    }
  }
}

export function createLocalTracks() {
  const enableCompanionMode = useConfigStore.getState().enableCompanionMode;
  if (!enableCompanionMode) {
    const audioInputDeviceId =
      useConfigStore.getState().audioInputDeviceId ??
      useBaseStore.getState().audioInputDevice?.deviceId;
    createLocalTrack('audio', audioInputDeviceId);
  }
  const sessionType = useConfigStore.getState().sessionType;
  if (sessionType === SESSION_TYPE.VIDEO) {
    const videoInputDeviceIdP1 = useConfigStore.getState().videoInputDeviceId;
    const videoInputDeviceIdP2 =
      useBaseStore.getState().videoInputDevice?.deviceId;
    const initialCameraFacingP1 = useConfigStore.getState().initialCameraFacing;
    const initialCameraFacingP2 = useBaseStore.getState().cameraFacing;

    if (videoInputDeviceIdP1) {
      createLocalTrack('video', videoInputDeviceIdP1);
    } else if (initialCameraFacingP1) {
      createLocalTrack('video', null, initialCameraFacingP2);
    } else {
      createLocalTrack('video', videoInputDeviceIdP2, initialCameraFacingP2);
    }
  }
}

export function updateAudioInputDevice(deviceId: string) {
  const audioInputDevices = useBaseStore
    .getState()
    .audioInputDevices.filter(device => device.deviceId !== '');
  if (audioInputDevices.length > 0) {
    createLocalTrack('audio', deviceId);
  }
}

export function updateAudioInputDeviceState(
  device?: AudioInputDevice,
  skipSubscribe = false,
) {
  const isMobileSDK = useBaseStore.getState().isMobileSDK();
  if (isMobileSDK) {
    return;
  }
  if (device) {
    useBaseStore.setState({
      audioInputDevice: device
        ? { ...device.toJSON(), skipSubscribe }
        : undefined,
    });
  }
}

export async function updateVideoInputDevice(
  deviceId: string | null,
  cameraFacing?: CameraFacingInternal,
) {
  const videoInputDevices = useBaseStore
    .getState()
    .videoInputDevices.filter(device => device.deviceId !== '');
  if (videoInputDevices.length > 0) {
    createLocalTrack('video', deviceId, cameraFacing);
  } else if (deviceId === null) {
    const videoTrack = getLocalTrack(MEDIA_TYPE.VIDEO)?.originalTrack;
    if (videoTrack) {
      await videoTrack.dispose();
      const conference = useConferenceStore.getState().conference;
      if (conference) {
        conference.replaceTrack(videoTrack, null);
      }
    }
    createLocalTrack('video', deviceId, cameraFacing);
  }
}

// TODO: Maybe move this function to web only.
export function updateVideoInputDeviceState(
  device?: VideoInputDevice,
  skipSubscribe = false,
) {
  const isMobileSDK = useBaseStore.getState().isMobileSDK();
  if (isMobileSDK) {
    return;
  }
  if (device) {
    const deviceJson = device.toJSON ? device.toJSON() : device;
    useBaseStore.setState({
      videoInputDevice: { ...deviceJson, skipSubscribe },
    });
  }
}

export function updateAudioOutputDevice(deviceId: string) {
  if (!JitsiMeetJS.mediaDevices.isDeviceChangeAvailable('output')) {
    console.warn('Adjusting audio output is not supported');
    return;
  }
  JitsiMeetJS.mediaDevices.setAudioOutputDevice(deviceId).then(() => {
    const audioOutputDevices = useBaseStore.getState().audioOutputDevices;
    const device = audioOutputDevices.find(d => d.deviceId === deviceId);
    if (device) {
      useBaseStore.setState({
        audioOutputDevice: device,
      });
    }
  });
}
